import { ETextStyleVariant, GeneralInstantBookIcon, Text } from '@outdoorsyco/bonfire';
import { useIntl } from 'react-intl';

import { defaultLocale } from '@/config/locales';
import { formatCurrency } from '@/utility/currency';
import { TAutoRentalTile } from '@/utility/mapAutoSearchResultToTile';
import { IRentalTile } from '@/utility/mapSearchResultToTile';

type TListingTileTotalPriceProps = {
  listingTile: IRentalTile | TAutoRentalTile;
  className?: string;
};

export const ListingTileTotalPrice = ({
  listingTile: { instantBook = false, currency = defaultLocale.base_currency, price },
  className = '',
}: TListingTileTotalPriceProps) => {
  const intl = useIntl();

  if (!price?.estimatedPriceBreakdown) return null;

  const hasDiscount =
    price.estimatedPriceBreakdown.total < price.estimatedPriceBreakdown.original_total;

  const formattedOriginalTotalEstimatedPrice = formatCurrency({
    currency,
    digits: 0,
    maxDigits: 0,
    priceInCents: price.estimatedPriceBreakdown.original_total,
  });

  const formattedTotalEstimatedPrice = formatCurrency({
    currency,
    digits: 0,
    maxDigits: 0,
    priceInCents: price.estimatedPriceBreakdown.total,
  });

  return (
    <div className={`flex items-center flex-nowrap gap-1 ${className}`}>
      {instantBook && (
        <GeneralInstantBookIcon
          aria-label={intl.formatMessage({
            description: '[Price]: screen reader message for instant book',
            defaultMessage: 'This listing allows instant booking.',
            id: 'ZpLmIK',
          })}
          className="w-4 h-4 text-canvas-300 shrink-0"
        />
      )}

      {hasDiscount && (
        <Text
          component="div"
          variant={ETextStyleVariant.MediumBold}
          aria-label={intl.formatMessage(
            {
              description: '[Price]: screen reader message for original total estimated price',
              defaultMessage: '{total} estimated total pre-tax before discount',
              id: 'e4XVl9',
            },
            { total: formattedOriginalTotalEstimatedPrice },
          )}
          className="line-through text-gray-300/75">
          {formattedOriginalTotalEstimatedPrice}
        </Text>
      )}

      <Text
        component="div"
        variant={ETextStyleVariant.MediumBold}
        aria-label={intl.formatMessage(
          {
            description: '[Price]: screen reader message for total estimated price',
            defaultMessage: '{total} estimated total pre-tax',
            id: 'o3DL1J',
          },
          { total: formattedTotalEstimatedPrice },
        )}>
        {formattedTotalEstimatedPrice}
      </Text>

      {price.estimatedPriceBreakdown.duration > 0 && (
        <Text aria-hidden variant={ETextStyleVariant.MediumRegular} className="truncate">
          {intl.formatMessage(
            {
              defaultMessage: 'for {nights, plural, one {# night} other {# nights}}',
              id: 'jzRwv5',
              description: '[Price]: total price formatting for a listing',
            },
            { nights: price.estimatedPriceBreakdown.duration },
          )}
        </Text>
      )}
    </div>
  );
};
